<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-02-28 (0.1.0) (jhoward)
 *   - Add DisplayPrizes.vue component to show offer prizes with selection logic.
 *
 * 2026-03-02 (0.1.1) (jhoward)
 *   - Show material-form-radio only if prizes are selectable.
 *   - Add scoped CSS for list styling.
 *
 * 2026-03-11 (0.2.0) (jhoward)
 *   - Add quantity dropdown for prize selection.
 *   - Add readable date formatting for prize start times.
 *
 * 2026-03-13 (0.2.1) (jhoward)
 *   - Switch date formatting to use getDateFromEngage.
 *   - Emit default selected prize if only one prize is available.
 *   - Improve prize display layout and grid styling.
 *
 * 2026-03-17 (0.3.0) (jhoward)
 *   - Add display of 'Sold Out' and available count for prizes in development environments.
 *   - Disable radio button for prizes that cannot be selected.
 *   - Import useAppStore and use for environment checks.
 *
 * 2026-09-08 (0.3.1) (jhoward)
 *   - Delegate isPrizeSelected() to src\helpers\getSelectedPrize.ts. The CASINO_PRIZE_ID matching rule is now also
 *     needed to name the redeemed prize on the offer status badge, and it is too easy to get subtly wrong to keep
 *     two copies of.
 *
 * 2026-10-06 (0.3.2) (jhoward)
 *   - Move date formatting to srchelpersgetReadablePrizeDate.ts, shared with the redeemed offer status.
 */

import MaterialFormRadio from '@framework/components/material/MaterialFormRadio.vue';
import type { SelectOption } from '@framework/components/material/MaterialFormSelect.vue';
import MaterialFormSelect from '@framework/components/material/MaterialFormSelect.vue';
import type { PropType } from 'vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getReadablePrizeDate from '@/helpers/getReadablePrizeDate';
import { isPrizeSelected as isPrizeSelectedForOffer } from '@/helpers/getSelectedPrize';
import useAppStore from '@/store';

const emits = defineEmits({
  'update:selectedPrizeId': (prizeId: string) => true,
  'update:selectedPrizeQuantity': (prizeQuantity: string) => true,
});

const props = defineProps({
  offer: {
    type: Object as PropType<OpcOffer>,
    required: true,
  },
  onlySelected: {
    type: Boolean,
    default: false,
  },
  prizesSelectable: {
    type: Boolean,
    default: false,
  },
});
const appStore = useAppStore();

function isPrizeSelected(prizeId: string): boolean {
  return isPrizeSelectedForOffer(props.offer, prizeId);
}

const quantityDropDowns: SelectOption[] = [];
for (let i = 0; i < props.offer?.PRIZE_MAX_QUANTITY; i++) {
  quantityDropDowns.push({
    label: (i + 1).toString(),
    value: (i + 1).toString(),
    selected: i + 1 === props.offer?.PRIZE_MAX_QUANTITY,
  });
}
const defaultPrizeId = props.offer?._PRIZES?.length === 1 ? props.offer._PRIZES[0].CASINO_PRIZE_ID : null;
if (defaultPrizeId) {
  emits('update:selectedPrizeId', defaultPrizeId);
}
</script>

<template>
  <div class="prize--container" :class="{ 'prize--container-grid': prizesSelectable }" v-if="offer._PRIZES?.length">
    <p v-if="prizesSelectable">Make selection below before redeeming:</p>
    <ul>
      <li
        v-for="prize in offer._PRIZES"
        :key="prize.CASINO_PRIZE_ID"
        v-show="!onlySelected || isPrizeSelected(prize.CASINO_PRIZE_ID)"
      >
        <material-form-radio
          v-if="prizesSelectable"
          :default-checked="defaultPrizeId === prize.CASINO_PRIZE_ID"
          name="prize"
          :is-disabled="prize.CAN_BE_SELECTED === 'NO'"
          :value="prize.CASINO_PRIZE_ID"
          @update:checked="$event ? emits('update:selectedPrizeId', prize.CASINO_PRIZE_ID) : null"
        ></material-form-radio>
        <span>
          {{ prize.CASINO_PRIZE_NAME }} <br />
          {{ getReadablePrizeDate(prize.PRIZE_START_TS) }}
          <span v-if="prize.CAN_BE_SELECTED === 'NO'"><br />Sold Out</span>
          <span v-if="isPrizeSelected(prize.CASINO_PRIZE_ID)"><br />Reserved {{ offer.PRIZE_QUANTITY_RESERVED }}</span>
          <span v-if="['staging', 'development', 'local'].includes(appStore.environment)"
            ><br />{{ prize.PRIZE_BOOKING_AVAIL }} available</span
          >
        </span>
      </li>
    </ul>
    <br />
    <material-form-select
      v-if="prizesSelectable"
      name="quantity"
      label="Quantity"
      :options="quantityDropDowns"
      @update:value="emits('update:selectedPrizeQuantity', $event)"
    ></material-form-select>
  </div>
</template>

<style scoped>
ul {
  list-style-type: none;
  padding-left: 0;
}

.card--body ul {
  margin: 0 -0.5em;
}

.card--body li {
  padding: 0.25em 0.5em;
}

.dialog--body li {
  padding: 0.25em 1.5em;
}

.prize--container-grid li {
  display: grid;
  grid-template-columns: 2em 1fr;
  gap: 0.5em;
  align-items: center;
}

.dialog--body .radio--container {
  margin: 0;
  padding: 0;
}

.prize--container-grid .radio--container {
  width: initial;
}

li:nth-child(even) {
  background-color: #eee;
}
</style>
